import { parseAtUri } from '@/lib/atUri'

const STANDARD_DOCUMENT_COLLECTION = 'site.standard.document'
const STANDARD_PUBLICATION_COLLECTION = 'site.standard.publication'

export interface StandardDocumentRef {
    did: string
    rkey: string
}

export interface StandardPublicationRef {
    did: string
    rkey: string
    /** The full AT URI — needed by useSubscriptionToggle. */
    uri: string
}

/**
 * Returns the first AT URI ref in `associatedRefs` whose collection is
 * `site.standard.document`, parsed into its DID + rkey components.
 * Returns null when no such ref exists, the input is empty/undefined, or all
 * candidates fail to parse.
 */
export function parseStandardDocumentUri(
    associatedRefs: ReadonlyArray<{ uri: string }> | undefined
): StandardDocumentRef | null {
    if (!associatedRefs || associatedRefs.length === 0) return null

    for (const ref of associatedRefs) {
        try {
            const parsed = parseAtUri(ref.uri)
            if (parsed.collection === STANDARD_DOCUMENT_COLLECTION) {
                if (!parsed.repo.startsWith('did:')) continue
                return { did: parsed.repo, rkey: parsed.rkey }
            }
        } catch {
            // Malformed URI — skip and continue scanning the rest of the list.
        }
    }

    return null
}

/**
 * Returns the first AT URI ref in `associatedRefs` whose collection is
 * `site.standard.publication`, parsed into its DID, rkey, and full URI.
 * Returns null when no such ref exists, the input is empty/undefined, or all
 * candidates fail to parse.
 */
export function parseStandardPublicationUri(
    associatedRefs: ReadonlyArray<{ uri: string }> | undefined
): StandardPublicationRef | null {
    if (!associatedRefs || associatedRefs.length === 0) return null

    for (const ref of associatedRefs) {
        try {
            const parsed = parseAtUri(ref.uri)
            if (parsed.collection !== STANDARD_PUBLICATION_COLLECTION) continue
            if (!parsed.repo.startsWith('did:')) continue
            return { did: parsed.repo, rkey: parsed.rkey, uri: ref.uri }
        } catch {
            // Malformed URI — skip and continue scanning the rest of the list.
        }
    }

    return null
}
